import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { BusinessCard } from '@/components/BusinessCard';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { useCountry } from '@/lib/countryContext';
import { businessesAPI, categoriesAPI, usersAPI } from '@/lib/api';
import { openExternalBookviaFlow, isNativeApp } from '@/lib/capacitor';import { getSelectedCity, setSelectedCity } from '@/lib/selectedCity';
import { Search, SlidersHorizontal, MapPin, X, Filter, List, Map as MapIcon, Star, ArrowRight, ChevronRight, Sparkles, ArrowLeft } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { SearchLeafletMap } from '@/components/SearchLeafletMap';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { buildGroups, pickPopular, GROUP_BY_SLUG, normalizeServerTree, groupCategoriesBySuperGroup } from '@/lib/categoryGroups';

function catToPascal(str) {
  if (!str) return '';
  return String(str)
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}

export default function SearchPage() {
  const { t, language } = useI18n();
  const { isAuthenticated } = useAuth();
  const { countryCode } = useCountry();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [serverTree, setServerTree] = useState(null);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [hoveredBiz, setHoveredBiz] = useState(null);
  // When the user picks a super-category with more than one leaf on the
  // categories browse screen we expand it inline instead of navigating away
  // — this keeps the "10 super-cats + Populares" layout compact on desktop.
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Filters
  const [query, setQuery] = useState(searchParams.get('q') || '');
  // City: URL param wins → falls back to the globally-persisted user city so
  // navigating to /search from anywhere respects the previous pick.
  const [city, setCity] = useState(() => searchParams.get('city') || getSelectedCity() || '');
  const [categoryId, setCategoryId] = useState(searchParams.get('category') || '');
  const [minRating, setMinRating] = useState([parseFloat(searchParams.get('rating')) || 0]);
  const [requiresDeposit, setRequiresDeposit] = useState(searchParams.get('deposit') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'relevance');
  // Price range chip: 'all' | 'cheap' | 'mid' | 'high' | 'premium'
  const [priceRange, setPriceRange] = useState(searchParams.get('price') || 'all');
  const [onlyFeatured, setOnlyFeatured] = useState(searchParams.get('featured') === 'true');
  const [openNow, setOpenNow] = useState(searchParams.get('open_now') === 'true');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locatingUser, setLocatingUser] = useState(false);
  // Radius (in km) applied to the "Cerca de ti" mode. Default 25 km so a
  // Monterrey user doesn't get sent to Nuevo Laredo (~200 km away).
  const [nearRadiusKm, setNearRadiusKm] = useState(() => {
    const fromUrl = parseInt(searchParams.get('radius'), 10);
    return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 25;
  });

  useEffect(() => {
    loadCategories();
    loadCities();
    loadFavorites();
    loadFeatured();
  }, [countryCode]);

  // Keep the globally-persisted city in sync with the SearchPage filter so
  // when the user picks a city here it's remembered on the Home / Categories
  // screens too. Never persists the "all cities" clear (falsy) to avoid
  // wiping a real pick with an empty transient filter.
  useEffect(() => {
    if (city && city.trim()) setSelectedCity(city.trim());
  }, [city]);

  useEffect(() => {
    loadBusinesses();
  }, [categoryId, city, minRating, requiresDeposit, sortBy, onlyFeatured, page, countryCode, userLocation, priceRange, nearRadiusKm]);

  // Show category browse view when no specific filters/query are active
  const showCategoriesView = !query && !city && (!categoryId || categoryId === 'all') &&
    !onlyFeatured && minRating[0] === 0 && requiresDeposit === 'all' && priceRange === 'all' &&
    sortBy === 'relevance' && !userLocation;

  const selectedCategory = useMemo(
    () => categories.find(c => c.id === categoryId || c.slug === categoryId),
    [categories, categoryId]
  );

  const handleCategorySelect = (cat) => {
    setCategoryId(cat.id);
    const params = new URLSearchParams(searchParams);
    params.set('category', cat.id);
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToCategories = () => {
    clearFilters();
    setSearchParams({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadFeatured = async () => {
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${baseUrl}/api/businesses/featured?limit=3${countryCode ? `&country_code=${countryCode}` : ''}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setFeaturedBusinesses(Array.isArray(data) ? data : []);
    } catch {
      setFeaturedBusinesses([]);
    }
  };

  const loadCategories = async () => {
    try {
      // Server-computed tree is preferred. We still keep the flat list in
      // state because the URL-driven `selectedCategory` lookup uses it.
      const [treeRes, flatRes] = await Promise.all([
        categoriesAPI.getGroupedTree().catch(() => null),
        categoriesAPI.getAll().catch(() => null),
      ]);
      const tree = treeRes?.data?.groups ? treeRes.data : null;
      setServerTree(tree);
      setCategories(Array.isArray(flatRes?.data) ? flatRes.data : []);
    } catch { setCategories([]); setServerTree(null); }
  };

  const loadCities = async () => {
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      // `with_businesses=true` = only cities that actually have approved
      // businesses (dedup'd + sorted by count). Without this flag we get
      // the full seeded catalog which surfaces empty cities in the filter.
      const res = await fetch(`${baseUrl}/api/cities?country_code=${countryCode}&with_businesses=true`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCities(Array.isArray(data) ? data : []);
    } catch {
      setCities([]);
    }
  };

  const loadFavorites = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await usersAPI.getFavorites();
      setFavorites((Array.isArray(res.data) ? res.data : []).map(b => b.id));
    } catch { setFavorites([]); }
  };

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      // Map priceRange chip to min/max values for the API
      const priceRanges = {
        cheap: { min: 0, max: 500 },
        mid: { min: 500, max: 1500 },
        high: { min: 1500, max: 3000 },
        premium: { min: 3000, max: 100000 },
      };
      const priceCfg = priceRanges[priceRange];
      const params = {
        query: query || undefined,
        category_id: categoryId && categoryId !== 'all' ? categoryId : undefined,
        city: city || undefined,
        country_code: countryCode || undefined,
        min_rating: minRating[0] > 0 ? minRating[0] : undefined,
        requires_deposit: requiresDeposit === 'yes' ? true : requiresDeposit === 'no' ? false : undefined,
        min_price: priceCfg?.min || undefined,
        max_price: priceCfg?.max || undefined,
        page, limit: 20,
      };
      if (userLocation) {
        params.user_lat = userLocation.lat;
        params.user_lng = userLocation.lng;
        if (sortBy === 'nearest') {
          params.sort = 'nearest';
          // Only apply the radius when the user is in "Near me" mode.
          if (nearRadiusKm > 0) params.max_distance_km = nearRadiusKm;
        }
      }
      const res = await businessesAPI.search(params);
      setBusinesses(Array.isArray(res.data) ? res.data : []);
    } catch { setBusinesses([]); }
    finally { setLoading(false); }
  };

  // Wrapper for city changes coming from any user-facing filter/input.
  // Selecting a specific city must OVERRIDE any active "near me" mode —
  // otherwise the proximity sort keeps surfacing results from a
  // different city and the user thinks the filter is broken.
  const applyCitySelection = (nextCity) => {
    const value = (nextCity || '').trim();
    setCity(value);
    if (value) {
      // Clear geolocation-based sort so the city filter wins.
      setUserLocation(null);
      if (sortBy === 'nearest') setSortBy('relevance');
    }
  };

  const requestLocation = () => {
    if (userLocation) {
      // Already have location, just switch to nearest sort and clear restrictive filters
      setSortBy('nearest');
      setCity('');
      setCategoryId('');
      setMinRating([0]);
      setOnlyFeatured(false);
      return;
    }
    // Fallback when location is denied or unavailable: in native app, default
    // to "Nuevo Laredo" so users who reject location permission still see
    // businesses instead of an empty screen.
    const applyDefaultCityFallback = () => {
      if (isNativeApp()) {
        setCity('Nuevo Laredo');
        setSortBy('relevance');
        toast.message(
          language === 'es'
            ? 'Mostrando negocios en Nuevo Laredo. Cambia la ciudad cuando quieras.'
            : 'Showing businesses in Nuevo Laredo. Change the city anytime.'
        );
      }
    };
    if (!navigator.geolocation) {
      toast.error(language === 'es' ? 'Tu navegador no soporta geolocalizacion' : 'Your browser does not support geolocation');
      applyDefaultCityFallback();
      return;
    }
    setLocatingUser(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSortBy('nearest');
        // Clear restrictive filters so more businesses appear
        setCity('');
        setCategoryId('');
        setMinRating([0]);
        setOnlyFeatured(false);
        setOpenNow(false);
        setLocatingUser(false);
        toast.success(language === 'es' ? 'Ubicacion obtenida. Mostrando negocios cercanos.' : 'Location obtained. Showing nearby businesses.');
      },
      () => {
        toast.error(language === 'es' ? 'No pudimos obtener tu ubicación. Permite el acceso en tu navegador.' : 'Could not get your location. Allow access in your browser.');
        setLocatingUser(false);
        applyDefaultCityFallback();
      },
      { timeout: 10000 }
    );
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (city) params.set('city', city);
    if (categoryId && categoryId !== 'all') params.set('category', categoryId);
    if (minRating[0] > 0) params.set('rating', minRating[0].toString());
    if (requiresDeposit !== 'all') params.set('deposit', requiresDeposit);
    if (priceRange !== 'all') params.set('price', priceRange);
    if (sortBy !== 'relevance') params.set('sort', sortBy);
    if (onlyFeatured) params.set('featured', 'true');
    setSearchParams(params);
    setFiltersOpen(false);
    loadBusinesses();
  };

  const handleFavorite = async (businessId) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      if (favorites.includes(businessId)) {
        await usersAPI.removeFavorite(businessId);
        setFavorites(prev => prev.filter(id => id !== businessId));
        toast.success(language === 'es' ? 'Eliminado de favoritos' : 'Removed from favorites');
      } else {
        await usersAPI.addFavorite(businessId);
        setFavorites(prev => [...prev, businessId]);
        toast.success(language === 'es' ? 'Agregado a favoritos' : 'Added to favorites');
      }
    } catch { toast.error('Error'); }
  };

  const clearFilters = () => {
    setQuery(''); setCity(''); setCategoryId(''); setMinRating([0]);
    setRequiresDeposit('all'); setSortBy('relevance');
    setPriceRange('all'); setOnlyFeatured(false); setOpenNow(false); setSearchParams({});
  };

  const hasActiveFilters = query || city || categoryId || minRating[0] > 0 || priceRange !== 'all' ||
    requiresDeposit !== 'all' || sortBy !== 'relevance' || onlyFeatured || openNow;

  const mappableBusinesses = useMemo(
    () => businesses.filter(b => b.latitude && b.longitude),
    [businesses]
  );

  // Filter results client-side: open now toggle
  const filteredBusinesses = useMemo(
    () => openNow ? businesses.filter(b => b.is_open_now === true) : businesses,
    [businesses, openNow]
  );

  const filteredMappable = useMemo(
    () => filteredBusinesses.filter(b => b.latitude && b.longitude),
    [filteredBusinesses]
  );

  const sortChips = [
    { id: 'relevance', label_es: 'Relevancia', label_en: 'Relevance' },
    { id: 'nearest', label_es: 'Más cercanos', label_en: 'Nearest' },
    { id: 'rating', label_es: 'Mejor calificados', label_en: 'Top rated' },
    { id: 'reviews', label_es: 'Más reseñas', label_en: 'Most reviews' },
    { id: 'newest', label_es: 'Más recientes', label_en: 'Newest' },
  ];

  const FilterContent = () => (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">{language === 'es' ? 'Categoría' : 'Category'}</Label>
        <Select value={categoryId} onValueChange={setCategoryId}>
          <SelectTrigger data-testid="filter-category" className="h-9">
            <SelectValue placeholder={language === 'es' ? 'Todas' : 'All'} />
          </SelectTrigger>
          <SelectContent className="max-h-[60vh]">
            <SelectItem value="all">{language === 'es' ? 'Todas las categorías' : 'All categories'}</SelectItem>
            {groupCategoriesBySuperGroup(categories, { includeHidden: false, includeOther: false }).map(group => (
              <SelectGroup key={group.slug}>
                <SelectLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {language === 'es' ? group.name_es : group.name_en}
                </SelectLabel>
                {group.items.map(cat => (
                  <SelectItem key={cat.id} value={cat.id} data-testid={`filter-category-option-${cat.slug}`}>
                    {language === 'es' ? cat.name_es : cat.name_en}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium">{language === 'es' ? 'Ciudad' : 'City'}</Label>
        <Select value={city || 'all'} onValueChange={(v) => applyCitySelection(v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="filter-city" className="h-9">
            <SelectValue placeholder={language === 'es' ? 'Todas' : 'All'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === 'es' ? 'Todas las ciudades' : 'All cities'}</SelectItem>
            {cities.map(c => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium">{language === 'es' ? 'Calificación mínima' : 'Min rating'}: {minRating[0]} <Star className="inline h-3 w-3 fill-yellow-400 text-yellow-400" /></Label>
        <Slider value={minRating} onValueChange={setMinRating} min={0} max={5} step={0.5} className="py-3" data-testid="filter-rating" />
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-medium">{language === 'es' ? 'Rango de precio' : 'Price range'}</Label>
        <div className="grid grid-cols-5 gap-1.5" data-testid="filter-price-chips">
          {[
            { id: 'all',     label: language === 'es' ? 'Todos'   : 'All'    },
            { id: 'cheap',   label: '$'    },
            { id: 'mid',     label: '$$'   },
            { id: 'high',    label: '$$$'  },
            { id: 'premium', label: '$$$$' },
          ].map(chip => (
            <button
              key={chip.id}
              type="button"
              onClick={() => setPriceRange(chip.id)}
              className={`h-9 rounded-full text-xs font-medium border transition-colors ${
                priceRange === chip.id
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-border/60 hover:bg-slate-50'
              }`}
              data-testid={`price-chip-${chip.id}`}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground">
          {priceRange === 'cheap'   && (language === 'es' ? 'Hasta $500'          : 'Up to $500')}
          {priceRange === 'mid'     && (language === 'es' ? '$500 – $1,500'       : '$500 – $1,500')}
          {priceRange === 'high'    && (language === 'es' ? '$1,500 – $3,000'     : '$1,500 – $3,000')}
          {priceRange === 'premium' && (language === 'es' ? 'Más de $3,000'       : '$3,000+')}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium">{language === 'es' ? 'Anticipo' : 'Deposit'}</Label>
        <Select value={requiresDeposit} onValueChange={setRequiresDeposit}>
          <SelectTrigger data-testid="filter-deposit" className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
            <SelectItem value="no">{language === 'es' ? 'Sin anticipo' : 'No deposit'}</SelectItem>
            <SelectItem value="yes">{language === 'es' ? 'Con anticipo' : 'With deposit'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2.5 pt-2">
        <div className="flex items-center gap-2">
          <Checkbox id="featured" checked={onlyFeatured} onCheckedChange={setOnlyFeatured} data-testid="filter-featured" />
          <Label htmlFor="featured" className="cursor-pointer text-xs">{language === 'es' ? 'Solo destacados' : 'Featured only'}</Label>
        </div>
      </div>

      <div className="flex gap-2 pt-3 border-t">
        {hasActiveFilters && (
          <Button variant="outline" onClick={clearFilters} className="flex-1 h-9 text-xs">
            <X className="h-3.5 w-3.5 mr-1" />{language === 'es' ? 'Limpiar' : 'Clear'}
          </Button>
        )}
        <Button onClick={handleSearch} className="flex-1 btn-coral h-9 text-xs">{language === 'es' ? 'Aplicar' : 'Apply'}</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-0 md:pt-20 bg-background" data-testid="search-page">

      {/* ── Search Header (normal flow — scrolls with content) ─────
          The search bar is part of the normal document flow. It appears
          at the top when the page loads and scrolls away naturally when
          the user scrolls down. */}
      <div className="bg-muted/30 border-b border-border">
        <div className="container-app py-3">
          <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-2 lg:items-center">
            {/* Main search row — stacks on mobile so both fields have real breathing room,
                merges into one row on sm+ for tablets and desktop. */}
            <div className="flex flex-col sm:flex-row flex-1 gap-2 min-w-0">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'es' ? 'Busca negocio, servicio o categoría' : 'Search business, service or category'}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-10 h-11 bg-white border-border/60"
                  data-testid="search-input"
                />
              </div>
              <div className="relative w-full sm:w-44 shrink-0">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'es' ? 'Ciudad' : 'City'}
                  value={city}
                  onChange={(e) => applyCitySelection(e.target.value)}
                  className="pl-10 h-11 bg-white border-border/60"
                  data-testid="search-city"
                />
              </div>
              <Button type="submit" className="h-11 btn-coral shrink-0 px-5 sm:px-4 gap-1.5 rounded-full" data-testid="search-button">
                <Search className="h-4 w-4" />
                <span>{language === 'es' ? 'Buscar' : 'Search'}</span>
              </Button>
            </div>

            {/* Secondary actions row - neutral pills */}
            <div className="flex items-center gap-1.5 lg:ml-auto">
              <button
                type="button"
                onClick={requestLocation}
                disabled={locatingUser}
                className={`h-9 px-3 inline-flex items-center gap-1.5 rounded-full text-xs font-medium transition-colors border ${
                  sortBy === 'nearest'
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-border/60 hover:bg-slate-50'
                }`}
                data-testid="nearby-button"
              >
                {locatingUser ? (
                  <span className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <MapPin className="h-3.5 w-3.5" />
                )}
                {locatingUser
                  ? (language === 'es' ? 'Ubicando...' : 'Locating...')
                  : (language === 'es' ? 'Cerca de ti' : 'Near you')}
              </button>

              {/* View Toggle - neutral segmented */}
              <div className="hidden md:inline-flex items-center bg-white border border-border/60 rounded-full p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`px-3 h-8 inline-flex items-center gap-1 text-xs font-medium rounded-full transition-colors ${
                    viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="view-list-toggle"
                >
                  <List className="h-3.5 w-3.5" />{language === 'es' ? 'Lista' : 'List'}
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('map')}
                  className={`px-3 h-8 inline-flex items-center gap-1 text-xs font-medium rounded-full transition-colors ${
                    viewMode === 'map' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="view-map-toggle"
                >
                  <MapIcon className="h-3.5 w-3.5" />{language === 'es' ? 'Mapa' : 'Map'}
                </button>
              </div>

              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-9 md:hidden rounded-full px-3 text-xs transition-colors ${
                      hasActiveFilters
                        ? 'border-[#F05D5E] text-[#F05D5E] bg-[#F05D5E]/5 hover:bg-[#F05D5E]/10'
                        : ''
                    }`}
                    data-testid="mobile-filters-button"
                  >
                    <Filter className="h-3.5 w-3.5 mr-1.5" />
                    {language === 'es' ? 'Filtros' : 'Filters'}
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader><SheetTitle>{language === 'es' ? 'Filtros' : 'Filters'}</SheetTitle></SheetHeader>
                  <div className="mt-4"><FilterContent /></div>
                </SheetContent>
              </Sheet>
            </div>
          </form>
        </div>
      </div>

      {/* ── Main Content ─────────────────────────────── */}
      <div className="container-app py-6">
        {showCategoriesView ? (
          /* ═════════════ CATEGORIES BROWSE VIEW ═════════════ */
          <div className="space-y-8" data-testid="categories-browse-view">
            {/* Hero copy */}
            <div className="text-center max-w-2xl mx-auto pt-2">
              <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-2 text-slate-900">
                {selectedGroup
                  ? (language === 'es' ? selectedGroup.name_es : selectedGroup.name_en)
                  : (language === 'es' ? '¿Qué necesitas hoy?' : 'What do you need today?')}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                {selectedGroup
                  ? (language === 'es'
                      ? 'Elige la especialidad que buscas.'
                      : 'Pick the specialty you need.')
                  : (language === 'es'
                      ? 'Elige una categoría para ver los negocios disponibles cerca de ti.'
                      : 'Pick a category to see available businesses near you.')}
              </p>
            </div>

            {selectedGroup ? (
              /* ── Sub-category drill-down (in-place) ─────────────── */
              <>
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => setSelectedGroup(null)}
                    className="h-9 px-4 inline-flex items-center gap-2 rounded-full text-sm font-medium bg-white border border-border/60 text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="back-to-groups"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {language === 'es' ? 'Volver a categorías' : 'Back to categories'}
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="subcategories-grid">
                  {selectedGroup.leafItems.map((cat) => (
                    <button
                      key={cat.id || cat.slug}
                      onClick={() => handleCategorySelect(cat)}
                      className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-border/50 hover:border-[#F05D5E]/40 transition-all hover:shadow-lg text-left"
                      data-testid={`subcategory-card-${cat.slug}`}
                    >
                      {cat.image_url ? (
                        <img
                          src={cat.image_url}
                          alt={language === 'es' ? cat.name_es : cat.name_en}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-[#F05D5E]/30 to-[#1a2844]/30" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                        <h3 className="font-heading font-bold text-white text-base sm:text-lg leading-tight line-clamp-2">
                          {language === 'es' ? cat.name_es : cat.name_en}
                        </h3>
                        <p className="text-xs text-white/85 mt-0.5">
                          {cat.business_count > 0
                            ? `${cat.business_count} ${language === 'es' ? (cat.business_count === 1 ? 'negocio' : 'negocios') : (cat.business_count === 1 ? 'business' : 'businesses')}`
                            : (language === 'es' ? 'Próximamente' : 'Coming soon')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                {/* ── Populares (chips) ──────────────────────────── */}
                {(() => {
                  const popular = serverTree?.populares?.length
                    ? serverTree.populares
                    : pickPopular(categories, 8);
                  if (!popular.length) return null;
                  return (
                    <div data-testid="populares-web">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="h-4 w-4 text-[#F05D5E]" strokeWidth={2.4} />
                        <h2 className="font-heading font-bold text-lg text-slate-900">
                          {language === 'es' ? 'Populares' : 'Popular'}
                        </h2>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {popular.map((cat) => {
                          const iconKey = catToPascal(cat.icon);
                          const IconCmp = (iconKey && LucideIcons[iconKey]) || Sparkles;
                          const label = language === 'es' ? cat.name_es : cat.name_en;
                          return (
                            <button
                              key={cat.slug}
                              type="button"
                              onClick={() => handleCategorySelect(cat)}
                              className="flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-white border border-border/60 hover:border-[#F05D5E]/50 hover:shadow-sm transition-all"
                              data-testid={`popular-chip-web-${cat.slug}`}
                            >
                              <span className="h-6 w-6 rounded-full bg-[#FEF3F1] grid place-items-center">
                                <IconCmp className="h-3.5 w-3.5 text-[#F05D5E]" strokeWidth={2.4} />
                              </span>
                              <span className="text-sm font-medium text-slate-800 whitespace-nowrap">
                                {label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* ── 10 Super-categories grid ─────────────────── */}
                <div>
                  <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">
                    {language === 'es' ? 'Explora por categoría' : 'Browse by category'}
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="categories-grid">
                    {categories.length === 0 && !serverTree ? (
                      Array.from({ length: 10 }).map((_, i) => (
                        <div key={i} className="aspect-[4/3] rounded-2xl bg-slate-100 animate-pulse" />
                      ))
                    ) : (
                      (serverTree
                        ? normalizeServerTree(serverTree).groups
                        : buildGroups(categories)
                      ).map((group) => {
                        const iconKey = catToPascal(group.icon);
                        const IconCmp = (iconKey && LucideIcons[iconKey]) || Sparkles;
                        const label = language === 'es' ? group.name_es : group.name_en;
                        const sub = language === 'es'
                          ? `${group.leafItems.length} tipo${group.leafItems.length === 1 ? '' : 's'}`
                          : `${group.leafItems.length} type${group.leafItems.length === 1 ? '' : 's'}`;
                        return (
                          <button
                            key={group.slug}
                            type="button"
                            onClick={() => {
                              // Skip drill-down when the super-cat only has one leaf.
                              if (group.leafItems.length === 1) {
                                handleCategorySelect(group.leafItems[0]);
                              } else {
                                setSelectedGroup(group);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }
                            }}
                            className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-border/50 hover:border-[#F05D5E]/40 transition-all hover:shadow-lg text-left"
                            data-testid={`group-card-web-${group.slug}`}
                          >
                            {group.image_url ? (
                              <img
                                src={group.image_url}
                                alt={label}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                loading="lazy"
                              />
                            ) : (
                              <div className="absolute inset-0 bg-gradient-to-br from-[#F05D5E]/30 to-[#1a2844]/30" />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent" />
                            <div className="absolute top-2 left-2 h-9 w-9 rounded-lg bg-white/90 backdrop-blur grid place-items-center">
                              <IconCmp className="h-4 w-4 text-[#F05D5E]" strokeWidth={2.2} />
                            </div>
                            {group.business_count > 0 && (
                              <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white text-[10px] font-semibold">
                                {group.business_count}
                              </div>
                            )}
                            <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                              <h3 className="font-heading font-bold text-white text-base sm:text-lg leading-tight line-clamp-2">
                                {label}
                              </h3>
                              <p className="text-xs text-white/85 mt-0.5">{sub}</p>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Quick actions row */}
            <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
              <button
                type="button"
                onClick={requestLocation}
                disabled={locatingUser}
                className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full text-xs font-medium bg-white border border-border/60 text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="cat-view-nearby"
              >
                <MapPin className="h-3.5 w-3.5" />
                {language === 'es' ? 'Mostrar negocios cerca de ti' : 'Show businesses near me'}
              </button>
              <button
                type="button"
                onClick={() => { setSortBy('rating'); }}
                className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full text-xs font-medium bg-white border border-border/60 text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="cat-view-all"
              >
                <Star className="h-3.5 w-3.5" />
                {language === 'es' ? 'Ver todos los negocios' : 'View all businesses'}
              </button>
            </div>

            {/* Featured businesses preview */}
            {featuredBusinesses.length > 0 && !selectedGroup && (
              <div className="pt-6 border-t border-border/40" data-testid="featured-section">
                <div className="flex items-end justify-between mb-4">
                  <div>
                    <h2 className="font-heading font-bold text-xl text-slate-900">
                      {language === 'es' ? 'Destacados de la semana' : 'Featured this week'}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {language === 'es' ? 'Los mejor calificados de la plataforma' : 'Top rated on the platform'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {featuredBusinesses.slice(0, 3).map(b => (
                    <BusinessCard
                      key={b.id}
                      business={b}
                      onFavorite={handleFavorite}
                      isFavorite={favorites.includes(b.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
        /* ═════════════ RESULTS VIEW (categories browse skipped) ═════════════ */
        <>
        {/* Breadcrumb when a category is selected */}
        {selectedCategory && (
          <div className="flex items-center gap-2 mb-4 text-sm" data-testid="category-breadcrumb">
            <button
              onClick={handleBackToCategories}
              className="text-muted-foreground hover:text-[#F05D5E] transition-colors"
              data-testid="back-to-categories"
            >
              {language === 'es' ? 'Categorías' : 'Categories'}
            </button>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium text-slate-900">
              {language === 'es' ? selectedCategory.name_es : selectedCategory.name_en}
            </span>
          </div>
        )}
        <div className="flex gap-6">

          {/* Desktop Filters Sidebar */}
          <aside className="hidden md:block w-56 flex-shrink-0">
            <div className="sticky top-24">
              <h3 className="font-heading font-bold text-sm mb-4 flex items-center gap-1.5">
                <SlidersHorizontal className="h-4 w-4" />
                {language === 'es' ? 'Filtros' : 'Filters'}
                {hasActiveFilters && <Badge variant="outline" className="text-[10px] h-4 px-1">{language === 'es' ? 'Activos' : 'Active'}</Badge>}
              </h3>
              <FilterContent />
            </div>
          </aside>

          {/* Results Area */}
          <main className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h1 className="text-xl font-heading font-bold">
                  {sortBy === 'nearest' && userLocation
                    ? (language === 'es' ? 'Negocios cerca de ti' : 'Businesses near you')
                    : query ? `"${query}"` : (language === 'es' ? 'Todos los negocios' : 'All businesses')}
                </h1>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {filteredBusinesses.length} {language === 'es' ? 'resultados' : 'results'}
                  {city && ` ${language === 'es' ? 'en' : 'in'} ${city}`}
                  {openNow && ` · ${language === 'es' ? 'abiertos ahora' : 'open now'}`}
                  {sortBy === 'nearest' && userLocation && businesses.length > 0 && businesses[0]?.distance_km != null && (
                    <> &mdash; {language === 'es' ? 'el más cercano a' : 'closest at'} {businesses[0].distance_km} km</>
                  )}
                </p>
              </div>

              {/* Mobile View Toggle */}
              <div className="flex md:hidden items-center bg-white border border-border/60 rounded-full p-0.5">
                <button type="button" onClick={() => setViewMode('list')} className={`p-1.5 rounded-full ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}>
                  <List className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setViewMode('map')} className={`p-1.5 rounded-full ${viewMode === 'map' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}>
                  <MapIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Quick chips: Open now + Sort */}
            <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-border/50" data-testid="sort-chips-row">
              <button
                type="button"
                onClick={() => setOpenNow(v => !v)}
                className={`h-8 px-3 inline-flex items-center gap-1.5 rounded-full text-xs font-medium border transition-colors ${
                  openNow
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-slate-700 border-border/60 hover:bg-emerald-50 hover:border-emerald-300'
                }`}
                data-testid="open-now-chip"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${openNow ? 'bg-white' : 'bg-emerald-500'}`} />
                {language === 'es' ? 'Abierto ahora' : 'Open now'}
              </button>

              <span className="hidden sm:inline-block w-px h-5 bg-border/60 mx-1" />

              <span className="text-[11px] text-muted-foreground uppercase tracking-wider mr-1">
                {language === 'es' ? 'Ordenar:' : 'Sort:'}
              </span>
              {sortChips.map(chip => (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => setSortBy(chip.id)}
                  className={`h-8 px-3 inline-flex items-center rounded-full text-xs font-medium border transition-colors ${
                    sortBy === chip.id
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-border/60 hover:bg-slate-50'
                  }`}
                  data-testid={`sort-chip-${chip.id}`}
                >
                  {language === 'es' ? chip.label_es : chip.label_en}
                </button>
              ))}
            </div>

            {/* Radius picker — visible whenever "Cerca de ti" is active */}
            {sortBy === 'nearest' && userLocation && (
              <div className="mb-3 p-3 bg-white dark:bg-[#1E1A17] border border-border/60 rounded-lg" data-testid="radius-picker">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    {language === 'es' ? 'Radio de búsqueda' : 'Search radius'}
                  </span>
                  <span className="text-sm font-bold text-[#F05D5E]" data-testid="radius-value">
                    {nearRadiusKm} km
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={100}
                  step={1}
                  value={nearRadiusKm}
                  onChange={(e) => setNearRadiusKm(parseInt(e.target.value, 10))}
                  className="w-full accent-[#F05D5E]"
                  data-testid="radius-slider"
                />
                <div className="flex justify-between mt-2 gap-1">
                  {[5, 10, 25, 50, 100].map((km) => (
                    <button
                      key={km}
                      type="button"
                      onClick={() => setNearRadiusKm(km)}
                      className={`flex-1 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                        nearRadiusKm === km
                          ? 'bg-[#F05D5E] text-white border-[#F05D5E]'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-[#F05D5E]/40 dark:bg-[#1E1A17] dark:text-slate-300 dark:border-slate-700'
                      }`}
                      data-testid={`radius-preset-${km}`}
                    >
                      {km} km
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Proximity info banner */}
            {sortBy === 'nearest' && userLocation && !loading && businesses.length > 0 && (
              <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300" data-testid="proximity-banner">
                <MapPin className="h-4 w-4 shrink-0" />
                <span>
                  {(() => {
                    const withDist = businesses.filter(b => b.distance_km != null);
                    const closestDist = withDist.length > 0 ? withDist[0].distance_km : null;
                    const farthestDist = withDist.length > 0 ? withDist[withDist.length - 1].distance_km : null;
                    if (withDist.length === 0) {
                      return language === 'es'
                        ? 'Los negocios mostrados aún no han configurado su ubicación exacta.'
                        : 'Shown businesses have not set their exact location yet.';
                    }
                    return language === 'es'
                      ? `Mostrando ${withDist.length} negocios de ${closestDist} km a ${farthestDist} km de distancia.`
                      : `Showing ${withDist.length} businesses from ${closestDist} km to ${farthestDist} km away.`;
                  })()}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-7 text-xs text-emerald-600 hover:text-emerald-800"
                  onClick={() => { setSortBy('relevance'); setUserLocation(null); }}
                  data-testid="clear-proximity"
                >
                  <X className="h-3 w-3 mr-1" />{language === 'es' ? 'Quitar' : 'Clear'}
                </Button>
              </div>
            )}

            {/* No results with proximity */}
            {sortBy === 'nearest' && userLocation && !loading && businesses.length === 0 && (
              <div className="text-center py-12">
                <MapPin className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h2 className="text-lg font-heading font-bold mb-2">
                  {language === 'es' ? 'No encontramos negocios cerca de ti' : 'No businesses found near you'}
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {language === 'es'
                    ? 'Intenta buscar en otra ciudad o quita el filtro de cercanía.'
                    : 'Try searching in another city or remove the proximity filter.'}
                </p>
                <Button variant="outline" onClick={() => { setSortBy('relevance'); setUserLocation(null); }}>
                  {language === 'es' ? 'Ver todos los negocios' : 'View all businesses'}
                </Button>
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1,2,3,4].map(i => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="aspect-[4/3]" />
                    <CardContent className="p-4 space-y-2">
                      <Skeleton className="h-5 w-3/4" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : viewMode === 'list' ? (
              /* ── List View ──────────────────────────── */
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="results-list">
                {filteredBusinesses.length > 0 ? filteredBusinesses.map(business => (
                  <div key={business.id} onMouseEnter={() => setHoveredBiz(business.id)} onMouseLeave={() => setHoveredBiz(null)}>
                    <BusinessCard business={business} onFavorite={handleFavorite} isFavorite={favorites.includes(business.id)} />
                  </div>
                )) : (
                  <div className="col-span-full">
                    <Card className="p-8 sm:p-12 text-center border-dashed" data-testid="no-results-card">
                      <div className="max-w-md mx-auto space-y-4">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#F05D5E]/10 mx-auto">
                          <MapPin className="h-8 w-8 text-[#F05D5E]" />
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg mb-1">
                            {language === 'es'
                              ? (city && query
                                ? `No hay negocios de "${query}" en ${city}`
                                : city
                                  ? `Aún no hay negocios en ${city}`
                                  : query
                                    ? `No encontramos resultados para "${query}"`
                                    : 'No encontramos resultados')
                              : (city && query
                                ? `No "${query}" businesses in ${city}`
                                : city
                                  ? `No businesses in ${city} yet`
                                  : query
                                    ? `No results for "${query}"`
                                    : 'No results found')}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {language === 'es'
                              ? 'Estamos creciendo. Pronto habrá más opciones disponibles.'
                              : "We're growing. More options will be available soon."}
                          </p>
                        </div>

                        {/* Smart hint: if the empty state is caused by the
                            "Open now" toggle, tell the user there ARE matches
                            they're just filtering out. `businesses` is the
                            pre-`openNow` list; `filteredBusinesses` is post. */}
                        {openNow && businesses.length > 0 && (
                          <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-3 flex items-start gap-3 text-left">
                            <div className="mt-0.5 h-6 w-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                              <Search className="h-3.5 w-3.5" />
                            </div>
                            <div className="text-sm flex-1">
                              <p className="font-medium text-emerald-900 dark:text-emerald-100">
                                {language === 'es'
                                  ? `Hay ${businesses.length} ${businesses.length === 1 ? 'negocio' : 'negocios'} que coincide${businesses.length === 1 ? '' : 'n'}, pero ${businesses.length === 1 ? 'está cerrado' : 'están cerrados'} ahora`
                                  : `${businesses.length} match${businesses.length === 1 ? '' : 'es'} — currently closed`}
                              </p>
                              <button
                                type="button"
                                onClick={() => setOpenNow(false)}
                                className="text-emerald-700 dark:text-emerald-300 underline underline-offset-2 font-medium text-sm mt-1"
                                data-testid="disable-open-now-btn"
                              >
                                {language === 'es' ? 'Quitar filtro "Abierto ahora"' : 'Remove "Open now" filter'}
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-2">
                          {hasActiveFilters && (
                            <Button onClick={clearFilters} variant="outline" size="sm" data-testid="clear-filters-btn">
                              <X className="h-3.5 w-3.5 mr-1" />
                              {language === 'es' ? 'Limpiar filtros' : 'Clear filters'}
                            </Button>
                          )}
                          <Button
                            onClick={() => openExternalBookviaFlow(navigate, '/business/register')}
                            size="sm"
                            className="btn-coral"
                            data-testid="register-business-empty-cta"
                          >
                            {language === 'es' ? '¿Tienes un negocio? Regístralo aquí' : 'Have a business? Register it here'}
                            <ArrowRight className="h-3.5 w-3.5 ml-1" />
                          </Button>
                        </div>
                      </div>

                    </Card>
                  </div>
                )}
              </div>
            ) : (
              /* ── Map View ───────────────────────────── */
              <div className="grid lg:grid-cols-2 gap-4" data-testid="results-map-view">
                {/* Map */}
                <div className="h-[500px] lg:h-[calc(100vh-200px)] rounded-xl overflow-hidden border sticky top-24" data-testid="search-map">
                  <SearchLeafletMap
                    businesses={filteredMappable}
                    navigate={navigate}
                    userLocation={userLocation}
                    radiusKm={sortBy === 'nearest' ? nearRadiusKm : 0}
                  />
                </div>

                {/* Side List */}
                <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto pr-1">
                  {filteredBusinesses.length > 0 ? filteredBusinesses.map(biz => (
                    <Card
                      key={biz.id}
                      className={`cursor-pointer transition-all hover:shadow-md ${hoveredBiz === biz.id ? 'border-[#F05D5E] shadow-md' : 'border-border/60'}`}
                      onClick={() => navigate(`/business/${biz.slug}`)}
                      onMouseEnter={() => setHoveredBiz(biz.id)}
                      onMouseLeave={() => setHoveredBiz(null)}
                      data-testid={`map-result-${biz.id}`}
                    >
                      <CardContent className="p-3 flex gap-3">
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                          <img
                            src={biz.photos?.[0] || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=200'}
                            alt={biz.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-sm truncate">{biz.name}</h3>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            <MapPin className="inline h-3 w-3 mr-0.5" />{biz.address}, {biz.city}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {biz.rating > 0 && (
                              <span className="flex items-center gap-0.5 text-xs">
                                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                <span className="font-medium">{biz.rating.toFixed(1)}</span>
                                <span className="text-muted-foreground">({biz.review_count})</span>
                              </span>
                            )}
                            {biz.category_name && <Badge variant="secondary" className="text-[10px] h-4">{biz.category_name}</Badge>}
                            {biz.next_available_text && (
                              <span className="text-[10px] font-medium text-emerald-600">{biz.next_available_text}</span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )) : (
                    <div className="text-center py-8">
                      <p className="text-sm text-muted-foreground">{language === 'es' ? 'No hay resultados' : 'No results'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
